import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  App,
  Row,
  Col,
  Select,
  DatePicker,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  EditOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excel';
import { getClientPlanReport } from '../../services/clients';
import { MEDIUM_OPTIONS } from '../../utils/constants';

const { Title, Text } = Typography;
const { useApp } = App;

// Opciones para filtros
const PLAN_TYPE_OPTIONS = [
  { value: 'basic', label: 'Básico' },
  { value: 'standard', label: 'Estándar' },
  { value: 'premium', label: 'Premium' },
];

const DURATION_OPTIONS = [
  { value: 'monthly', label: 'Mensual' },
  { value: 'annual', label: 'Anual' },
];

const DATE_TYPE_OPTIONS = [
  { value: 'vendido', label: 'Vendido' },
  { value: 'caducado', label: 'Caducado' },
];

// Reutilizar MEDIUM_OPTIONS para origen
const ORIGIN_OPTIONS = MEDIUM_OPTIONS;

const PlanReportPage = () => {
  const navigate = useNavigate();
  const { message } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Estado para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Estado para los filtros
  const [filters, setFilters] = useState({
    dateType: 'vendido',
    dateRange: null,
    planType: null,
    duration: null,
    origen: null,
  });

  // Función para obtener datos del backend
  const fetchData = useCallback(async (page = 1, pageSize = 10, currentFilters = filters) => {
    setLoading(true);
    try {
      // Construir parámetros para la API
      const params = {
        dateType: currentFilters.dateType || 'vendido',
        startDate: currentFilters.dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
        endDate: currentFilters.dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
        planType: currentFilters.planType || undefined,
        duration: currentFilters.duration || undefined,
        origen: currentFilters.origen || undefined,
        page: page,
        pageSize: pageSize,
      };

      // Limpiar parámetros undefined
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await getClientPlanReport(params);
      
      // Usar response.summary en lugar de response.items
      const summary = response.items.summary || [];
      const total = response.items.total || summary.length;
      
      // Transformar datos según la estructura del backend
      const transformedData = summary.map((item, index) => ({
        id: index + 1, // Usar índice como ID temporal
        plan: getPlanLabel(item.plan_type),
        duracion: getDurationLabel(item.duration),
        plan_type: item.plan_type,
        duration: item.duration,
        compras: parseInt(item.sold || item.compras || 0),
        vencidos: parseInt(item.expired || item.vencidos || 0),
        activos: parseInt(item.active || 0),
        total: parseFloat(item.total || 0),
      }));

      setData(transformedData);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: pageSize,
        total: total,
      }));

    } catch (error) {
      console.error('Error al cargar reporte de planes:', error);
      message.error('No se pudo cargar el reporte de planes');
      setData([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [message, filters]);

  // Función para obtener etiqueta del plan
  const getPlanLabel = (planType) => {
    switch(planType) {
      case 'basic': return 'Básico';
      case 'standard': return 'Estándar';
      case 'premium': return 'Premium';
      default: return planType;
    }
  };

  // Función para obtener etiqueta de duración
  const getDurationLabel = (duration) => {
    switch(duration) {
      case 'monthly': return 'Mensual';
      case 'annual': return 'Anual';
      default: return duration;
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    fetchData(1, pagination.pageSize);
  }, []);

  // Calcular totales
  const calculateTotals = (data) => {
    return {
      totalCompras: data.reduce((sum, item) => sum + (item.compras || 0), 0),
      totalVencidos: data.reduce((sum, item) => sum + (item.vencidos || 0), 0),
      totalVenta: data.reduce((sum, item) => sum + (item.total || 0), 0),
    };
  };

  const totals = calculateTotals(data);

  // Función para aplicar filtros
  const applyFilters = useCallback(() => {
    fetchData(1, pagination.pageSize, filters);
  }, [fetchData, filters, pagination.pageSize]);

  // Manejar cambio de filtros
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Limpiar filtros
  const handleClearFilters = () => {
    const resetFilters = {
      dateType: 'vendido',
      dateRange: null,
      planType: null,
      duration: null,
      origen: null,
    };
    setFilters(resetFilters);
    fetchData(1, pagination.pageSize, resetFilters);
    message.success('Filtros limpiados correctamente');
  };

  // Exportar a Excel
  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      // Obtener todos los datos sin paginación para exportar
      const exportParams = {
        dateType: filters.dateType || 'vendido',
        startDate: filters.dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
        endDate: filters.dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
        planType: filters.planType || undefined,
        duration: filters.duration || undefined,
        origen: filters.origen || undefined,
        page: 1,
        pageSize: 1000, // Limite alto para obtener todos los datos
      };

      // Limpiar parámetros undefined
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === undefined) {
          delete exportParams[key];
        }
      });

      const response = await getClientPlanReport(exportParams);
      const summary = response.items.summary || [];
      
      if (summary.length === 0) {
        message.warning('No hay datos para exportar');
        return;
      }

      // Transformar datos para Excel según la estructura del backend
      const excelData = summary.map((item, idx) => {
        
        return {
          '#': idx + 1,
          'Plan': getPlanLabel(item.plan_type),
          'Duracion': getDurationLabel(item.duration),
          'Compras': parseInt(item.sold || 0),
          'Vencidos': parseInt(item.expired || 0),
          'Venta Total': `$${parseFloat(item.total || 0).toFixed(2)}`,
        };
      });

      const columnWidths = [
        { wch: 5 },   // #
        { wch: 15 },  // Plan
        { wch: 15 },  // Periodicidad
        { wch: 10 },  // Vendidos
        { wch: 12 },  // Caducados
        { wch: 10 },  // Activos
        { wch: 15 },  // Venta Total
      ];

      exportToExcel(excelData, columnWidths, 'Reporte de Planes', 'reporte_planes');
      message.success('Reporte exportado a Excel correctamente');
    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      message.error('No se pudo exportar el reporte a Excel');
    } finally {
      setExporting(false);
    }
  };

  // Botón de PDF (sin funcionalidad)
  const handleExportToPDF = () => {
    setExportingPDF(true);
    setTimeout(() => {
      message.info('La exportación a PDF estará disponible próximamente');
      setExportingPDF(false);
    }, 1000);
  };

  // Manejar cambio de paginación
  const handleTableChange = (pag) => {
    setPagination(pag);
    fetchData(pag.current, pag.pageSize, filters);
  };

  // Columnas de la tabla (ajustadas a la estructura del backend)
  const columns = [
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
    },
    {
      title: 'Duración',
      dataIndex: 'duracion',
      key: 'duracion',
    },
    {
      title: 'Compras',
      dataIndex: 'compras',
      key: 'compras',
      align: 'right',
      render: (value) => <Text strong>{value || 0}</Text>,
    },
    {
      title: 'Vencidos',
      dataIndex: 'vencidos',
      key: 'vencidos',
      align: 'right',
      render: (value) => (
        <Text type={value > 0 ? 'danger' : 'success'}>
          {value || 0}
        </Text>
      ),
    },
    {
      title: 'Venta Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (value) => (
        <Text strong type="success">
          ${(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
  ];

  // Fila de totales
  const renderTableSummary = () => (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={2}>
          <Text strong>TOTALES</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          <Space>
            <ShoppingOutlined />
            <Text strong>{totals.totalCompras}</Text>
          </Space>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          <Space>
            <CalendarOutlined />
            <Text strong type={totals.totalVencidos > 0 ? 'danger' : 'success'}>
              {totals.totalVencidos}
            </Text>
          </Space>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <Space>
            <DollarOutlined />
            <Text strong type="success">
              ${totals.totalVenta.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </Text>
          </Space>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{ padding: 0, marginRight: 8 }}
            />
            <Title level={3} style={{ marginBottom: 0 }}>
              Reporte de Planes
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Tooltip title="Exportar a Excel">
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportToExcel}
                loading={exporting}
                disabled={exporting}
                aria-label="Exportar a Excel"
              />
            </Tooltip>
            <Tooltip title="Exportar a PDF (Próximamente)">
              <Button
                icon={<FilePdfOutlined />}
                onClick={handleExportToPDF}
                loading={exportingPDF}
                disabled={exportingPDF}
                aria-label="Exportar a PDF"
              />
            </Tooltip>
          </Space>
        }
      >
        {/* Filtros */}
        <Card
          type="inner"
          title="Filtros"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha de</span>
                <Select
                  placeholder="Seleccione..."
                  style={{ width: '100%' }}
                  value={filters.dateType}
                  onChange={(value) => handleFilterChange('dateType', value)}
                  options={DATE_TYPE_OPTIONS}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha Inicio</span>
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={filters.dateRange?.[0] || null}
                  onChange={(date) => {
                    if (date) {
                      const newRange = filters.dateRange
                        ? [date, filters.dateRange[1]]
                        : [date, null];
                      handleFilterChange('dateRange', newRange);
                    } else {
                      const newRange = filters.dateRange?.[1]
                        ? [null, filters.dateRange[1]]
                        : null;
                      handleFilterChange('dateRange', newRange);
                    }
                  }}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha Fin</span>
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={filters.dateRange?.[1] || null}
                  onChange={(date) => {
                    if (date) {
                      const newRange = filters.dateRange
                        ? [filters.dateRange[0], date]
                        : [null, date];
                      handleFilterChange('dateRange', newRange);
                    } else {
                      const newRange = filters.dateRange?.[0]
                        ? [filters.dateRange[0], null]
                        : null;
                      handleFilterChange('dateRange', newRange);
                    }
                  }}
                  disabledDate={(current) => {
                    if (filters.dateRange?.[0]) {
                      return current && current < filters.dateRange[0].startOf('day');
                    }
                    return false;
                  }}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Tipo de Plan</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.planType}
                  onChange={(value) => handleFilterChange('planType', value)}
                  allowClear
                  options={PLAN_TYPE_OPTIONS}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Duración</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.duration}
                  onChange={(value) => handleFilterChange('duration', value)}
                  allowClear
                  options={DURATION_OPTIONS}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Origen</span>
                <Select
                  placeholder="..."
                  style={{ width: '100%' }}
                  value={filters.origen}
                  onChange={(value) => handleFilterChange('origen', value)}
                  allowClear
                  options={ORIGIN_OPTIONS}
                />
              </Space>
            </Col>
          </Row>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={applyFilters}
                shape="circle"
                size="large"
              />
              <Button
                icon={<EditOutlined />}
                onClick={handleClearFilters}
                shape="circle"
                size="large"
              />
            </Space>
          </Row>
        </Card>

        {/* Tabla */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
          }}
          onChange={handleTableChange}
          summary={renderTableSummary}
        />
      </Card>
    </div>
  );
};

export default PlanReportPage;